import { generateEmailHash } from '@hyphengroup/cms-components/utils/emailHash';
import {
  CarInsurancePremiumedProduct,
  CarInsuranceProduct,
  SortType,
} from 'car-insurance/types';
import { getPremiumValue } from 'car-insurance/utils/getPremiumValue';
import { getSortOrder } from 'car-insurance/utils/getSortOrder';
import CryptoJS from 'crypto-js';
import { track, type TrackingData } from 'utils/tracking';
import type {
  TrackCIPolicySearchFormFieldPayload,
  SearchResultParameterPayload,
  TrackCIDriverInfoChangePayload,
  QuoteFormGetResultsPayload,
  QuoteFormLeadPayload,
} from './types';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

function configureSearchResultParameterString(
  params: SearchResultParameterPayload,
): string {
  const {
    age,
    brand,
    drivingExperience,
    gender,
    maritalStatus,
    model,
    ncd,
    numberOfClaims,
    year,
    occupationType,
    offPeakVehicle,
    offenceFreeDiscount,
    variant,
  } = params;

  return `brand-${brand}|model-${model}${variant ? `|cc-${variant}` : ''}|year-${year}|exp-${drivingExperience}|ncd-${ncd}|age-${age}|gender-${gender}|ms-${maritalStatus}|noc-${numberOfClaims}${offPeakVehicle ? `|opc-${offPeakVehicle}` : ''}${occupationType ? `|jobt-${occupationType}` : ''}${offenceFreeDiscount ? `|ofd-${offenceFreeDiscount}` : ''}`;
}

export function trackCISearchButton(
  params: SearchResultParameterPayload,
): void {
  const trackingData = {
    event: 'final_search',
    search_result_parameter: configureSearchResultParameterString(params),
    vertical_code: 'CI',
    component_type: 'search-module',
    component_title: 'CI Search Module_Search',
    action: 'click',
  };
  track(trackingData);
}

export function trackCIPolicySearchFormField({
  field,
  value,
}: TrackCIPolicySearchFormFieldPayload): void {
  const trackingData = {
    event: 'form_interact',
    lead_form_name: 'ci-policy-search-module-car-info',
    action: 'select',
    component_type: 'dropdown',
    component_title: field,
    vertical_code: 'CI',
    form_field_value: value,
  };
  track(trackingData);
}

export function trackCIEditQuoteTooltip(): void {
  const trackingData = {
    event: 'navigate',
    location: 'result page',
    component_type: 'tooltip',
    component_title: 'Driver Info Module_Default parameter tooltip',
    action: 'view',
    index: '',
  };
  track(trackingData);
}

export function trackCIEditQuotePencilButton(): void {
  const trackingData = {
    event: 'form_interact',
    lead_form_name: 'ci-policy-search-module-driver-info',
    action: 'click',
    component_type: 'search-module',
    component_title: 'Edit',
    vertical_code: 'CI',
    form_field_value: '',
  };
  track(trackingData);
}

export function trackCIDriverInfoChange({
  action = 'select',
  componentTitle,
  componentType,
  formType,
  value,
}: TrackCIDriverInfoChangePayload): void {
  const trackingData = {
    event: 'form_interact',
    lead_form_name: 'ci-policy-search-module-driver-info',
    action,
    component_type: componentType,
    component_title: componentTitle,
    vertical_code: 'CI',
    form_field_value: value,
    ...(formType && { form_type: formType }),
  };
  track(trackingData);
}

export function trackMaritalStatusChange(value: string): void {
  const formattedValue = value === 'S' ? 'Single' : 'Married';

  trackCIDriverInfoChange({
    componentTitle: 'Marital Status',
    componentType: 'button',
    value: formattedValue,
  });
}

export function trackCIEditSidebarButton(
  params: SearchResultParameterPayload,
): void {
  const trackingData = {
    event: 'final_search',
    search_result_parameter: configureSearchResultParameterString(params),
    vertical_code: 'CI',
    component_type: 'search-module',
    component_title: 'CI Search Module_Update Quote',
    action: 'click',
    step_number: '2',
    step_name: 'search-driver-info',
    is_final_step: 'true',
    is_lead_capture: 'false',
  };
  track(trackingData);
}

export function trackingCIProductCard(
  component_title:
    | 'Product Card_Buy'
    | 'Footer_Plan Details'
    | 'Footer_Reward Button'
    | string,
  event: 'product_click_apply' | 'product_interaction',
  selectedProviders: string[],
  currentSort: SortType,
  product: CarInsurancePremiumedProduct,
  index: number,
): TrackingData {
  return {
    event,
    component_type: 'product-card',
    component_title,
    action: 'click',
    filter_applied: {
      sort: `${currentSort.toLowerCase()}.${getSortOrder(currentSort)}`,
      providers: selectedProviders,
    },
    items: [
      {
        item_id: product.productUid,
        item_name: product.productName,
        item_brand: product.provider.providerName,
        index: index + 1,
        price: getPremiumValue(product),
        discount: product.premium?.discountValue
          ? (product.premium.discountValue * 10).toFixed(2)
          : '',
      },
    ],
  };
}

export function trackProductCardCTAButton(
  selectedProviders: string[],
  currentSort: SortType,
  product: CarInsurancePremiumedProduct,
  index: number,
  params: SearchResultParameterPayload,
): void {
  sessionAttributionTracker.recordApplyClick();
  const trackingData = {
    component_title: 'Product Card_Buy',
    event: 'product_click_apply',
    component_type: 'product-card',
    action: 'click',
    filter_applied: {
      sort: `${currentSort.toLowerCase()}.${getSortOrder(currentSort)}`,
      providers: selectedProviders,
    },
    items: [
      {
        item_id: product.productUid,
        item_name: product.productName,
        item_brand: product.provider.providerName,
        index: index + 1,
        price: getPremiumValue(product),
        discount: product.premium?.discountValue
          ? (product.premium.discountValue * 10).toFixed(2)
          : '',
      },
    ],
    search_result_parameter: configureSearchResultParameterString(params),
  };

  track(trackingData);
}

export function trackSnackBarContactUsButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'bottom-sticky',
    component_title: 'Contact Us Snackbar_Contact Us',
    action: 'click',
    index: '',
  };
  track(trackingData);
}

export function trackLeadFormSwitch({
  errorMessage,
  formFieldValue,
  success,
}: {
  formFieldValue: string;
  success: boolean;
  errorMessage: string;
}): void {
  const trackingData = {
    event: 'form_interact',
    lead_form_name: 'ci-rp-contact-us-form',
    form_type: 'lead form',
    action: 'select',
    component_type: 'radio-button',
    component_title: 'Preferred contact method',
    vertical_code: 'CI',
    form_field_value: formFieldValue,
    status: success ? 'success' : 'failure',
    error_message: errorMessage,
  };

  track(trackingData);
}

export function trackSnackBarCloseButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'bottom-sticky',
    component_title: 'Contact Us Snackbar_Close',
    action: 'click',
    index: '',
  };
  track(trackingData);
}

export function trackSnackbarWhatsappNowButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'overlay',
    component_title: 'Lead Collection Form_Step2_WhatsApp us now',
    action: 'click',
  };
  track(trackingData);
}

export function trackSnackbarCallNowButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'overlay',
    component_title: 'Lead Collection Form_Step2_Call us now',
    action: 'click',
  };
  track(trackingData);
}

export function trackSnackbarEmailNowButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'overlay',
    component_title: 'Lead Collection Form_Step2_Email us now',
    action: 'click',
  };
  track(trackingData);
}

export function trackWhatsappCTAButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'cta-button',
    component_title: 'Lead Collection Form_Step1_WhatsApp us now',
    action: 'click',
    index: '',
  };
  track(trackingData);
}

export function trackWhatsappContactUsButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'overlay',
    component_title: 'CTA_Contact us',
    action: 'click',
    index: '',
  };
  track(trackingData);
}

export function trackConfirmationModalWhatsappButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'overlay',
    component_title: 'Confirmation Modal_WhatsApp us now',
    action: 'click',
  };
  track(trackingData);
}

type ContactMeNowPayload = {
  consent: boolean;
  country: string;
  gender: string;
  email?: string;
  firstName: string;
  lastName?: string;
  phoneNumber?: string;
};

export function trackLeadFormContactMeNow({
  consent,
  country,
  gender,
  email,
  firstName,
  lastName,
  phoneNumber,
}: ContactMeNowPayload): void {
  const emailInLowerCase = email?.length ? email.toLowerCase() : '';
  const emailMd5 = email?.length ? generateEmailHash(emailInLowerCase) : '';
  const emailSha256 = email?.length
    ? CryptoJS.SHA256(emailInLowerCase).toString()
    : '';

  const firstNameSha256 = CryptoJS.SHA256(firstName).toString();
  const lastNameSha256 = lastName ? CryptoJS.SHA256(lastName).toString() : '';
  const phoneNumberSha256 = phoneNumber
    ? CryptoJS.SHA256(phoneNumber).toString()
    : '';
  const genderSha256 = CryptoJS.SHA256(gender).toString();
  const countrySha256 = CryptoJS.SHA256(country).toString();

  const userData = {
    email: emailInLowerCase || '',
    email_hash_md5: emailMd5,
    email_hash_sha256: emailSha256,
    first_name: firstName,
    first_name_sha256: firstNameSha256,
    last_name: lastName || '',
    gender: gender,
    gender_sha256: genderSha256,
    country: country,
    country_sha256: countrySha256,
    last_name_sha256: lastNameSha256,
    phone_number: phoneNumber || '',
    phone_number_sha256: phoneNumberSha256,
    client_ip_address: '',
  };

  const trackingData = {
    event: 'form_submit',
    lead_form_name: 'ci-rp-contact-us-form',
    form_type: 'lead form',
    action: 'submit',
    marketing_consent: consent ? 'yes' : 'no',
    form_submission_id: '',
    lead_id: '',
    reward_campaign_name: '',
    reward_campaign_id: '',
    reward_selected: '',
    user_profile: '',
    user_data: userData,
    userIdentities: structuredClone(userData),
  };
  track(trackingData);
}

type LeadFormRewardsPayload = {
  leadId: string | number;
  email: string;
  consent: boolean;
  product: CarInsuranceProduct;
  rewardProductId: string;
};

export function trackLeadFormRewards({
  leadId,
  email,
  consent,
  product,
  rewardProductId,
}: LeadFormRewardsPayload): void {
  const emailInLowerCase = email.toLowerCase();
  const emailMd5 = email?.length ? generateEmailHash(email) : '';
  const emailSha256 = email?.length
    ? CryptoJS.SHA256(emailInLowerCase).toString()
    : '';

  const userData = {
    email: emailInLowerCase || '',
    email_hash_md5: emailMd5,
    email_hash_sha256: emailSha256,
  };

  const trackingData = {
    event: 'reward_gateway',
    location: 'rewards page',
    step_number: '1',
    step_name: 'user-auth',
    intent: 'Lead Submitted',
    marketing_consent: consent ? 'yes' : 'no',
    lead_id: String(leadId),
    reward_campaign_name: '',
    reward_campaign_id: rewardProductId,
    reward_selected: '',
    user_data: userData,
    userIdentities: structuredClone(userData),
    items: [
      {
        item_id: product.productUid,
        item_name: product.productName,
        item_brand: product.provider.providerName,
      },
    ],
  };
  track(trackingData);
}

export function trackCILookingFileClaimButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'hero-image',
    component_title: 'CTA_Looking to file a claim',
    action: 'click',
  };
  track(trackingData);
}

export function trackCIGetQuoteButton(): void {
  const trackingData = {
    event: 'navigate',
    component_type: 'hero-image',
    component_title: 'Text Link_Get Quote',
    action: 'click',
    index: '',
  };
  track(trackingData);
}

export function trackCIQuoteFormContinueButton(): void {
  const trackingData = {
    event: 'form_interact',
    lead_form_name: 'ci-get-quote-form',
    form_type: 'lead form',
    action: 'next',
    component_type: 'cta-button',
    component_title: 'CTA_Continue to Contact Info',
    vertical_code: 'CI',
  };
  track(trackingData);
}

export function trackCIQuoteFormGetResultsButton({
  brand,
  carModel,
  carYear,
  variant,
  primaryInUse,
  isCTPL,
}: QuoteFormGetResultsPayload): void {
  const trackingData = {
    event: 'final_search',
    search_result_parameter: `brand-${brand}|model-${carModel}|year-${carYear}|var-${variant}|use-${primaryInUse}|ctpl-${isCTPL}`,
    vertical_code: 'CI',
    coupon: '',
    component_type: 'cta-button',
    component_title: 'Get Results',
    action: 'click',
    step_number: '2',
    step_name: 'contact-information',
    is_final_step: 'true',
    is_lead_capture: 'false',
  };
  track(trackingData);
}

export function trackCIQuoteFormLead({
  firstName,
  lastName,
  email,
  phoneNumber,
  marketingConsent,
  leadId,
  country,
}: QuoteFormLeadPayload): void {
  const emailInLowerCase = email.trim().toLowerCase();
  const emailMd5 = email?.length ? generateEmailHash(email) : '';
  const emailSha256 = email?.length
    ? CryptoJS.SHA256(emailInLowerCase).toString()
    : '';
  const firstNameInLowerCase = firstName.trim().toLowerCase();
  const lastNameInLowerCase = lastName.trim().toLowerCase();
  const firstNameSha256 = CryptoJS.SHA256(firstNameInLowerCase).toString();
  const lastNameSha256 = lastName
    ? CryptoJS.SHA256(lastNameInLowerCase).toString()
    : '';
  const phoneNumberWithoutZero = phoneNumber.trim().replace(/^0+/, '');
  const phoneNumberWithCode = `63${phoneNumberWithoutZero}`;
  const phoneNumberSha256 = phoneNumberWithCode
    ? CryptoJS.SHA256(phoneNumberWithCode).toString()
    : '';
  const countrySha256 = CryptoJS.SHA256(country).toString();

  const userData = {
    email: emailInLowerCase,
    email_hash_md5: emailMd5,
    email_hash_sha256: emailSha256,
    first_name: firstNameInLowerCase,
    first_name_sha256: firstNameSha256,
    last_name: lastNameInLowerCase,
    last_name_sha256: lastNameSha256,
    country: country,
    country_sha256: countrySha256,
    phone_number: phoneNumberWithCode,
    phone_number_sha256: phoneNumberSha256,
  };

  const trackingData = {
    event: 'form_submit',
    lead_form_name: 'ci-get-quote-form',
    form_type: 'lead form',
    action: 'submit',
    marketing_consent: marketingConsent,
    form_submission_id: '',
    lead_id: leadId,
    user_data: userData,
    userIdentities: structuredClone(userData),
  };
  track(trackingData);
}
